export type OrganizationKind = 'customer' | 'agency'
export type OrganizationRole = 'owner' | 'admin' | 'member'
export type ProjectAccessScope = 'all' | 'selected'
export type ProjectRole = 'owner' | 'admin' | 'editor'
export type ProjectAccessSource = 'platform_admin' | 'owner' | 'direct' | 'organization' | 'agency'

export interface OrganizationRef {
  pid: string
  name: string
  kind: OrganizationKind
}

/** How the signed-in user reaches one project (`GET /api/teams`). */
export interface ProjectAccess {
  source: ProjectAccessSource
  role: ProjectRole
  /** The agency organization for `agency` access. */
  via: { pid: string; name: string } | null
}

/** Unpaid projects in an agency organization; null for customer organizations. */
export interface DemoProjectUsage {
  used: number
  limit: number
}

/** One row of `GET /api/organizations`, also sent as `organizations` on `/api/user`. */
export interface OrganizationSummary {
  pid: string
  name: string
  kind: OrganizationKind
  role: OrganizationRole
  project_access: ProjectAccessScope
  demo_projects: DemoProjectUsage | null
}

export interface AgencyLink {
  id: number
  role: string
  status: 'active' | 'ended'
  created_at: string
  agency: { pid: string; name: string; logo: string | null }
  client: { pid: string; name: string }
}

/** `GET /api/organizations/{orgPid}`. */
export interface Organization {
  pid: string
  name: string
  kind: OrganizationKind
  role: OrganizationRole
  can: { manage_members: boolean; manage_settings: boolean }
  demo_projects: DemoProjectUsage | null
  agency_links: AgencyLink[]
}

/**
 * One row of `GET /api/organizations/{orgPid}/members`. Pending invitations
 * are listed too, with `invited: true` and the `invitation_id` that
 * `DELETE /api/organizations/{orgPid}/invitations/{id}` cancels.
 */
export interface OrganizationMember {
  user: { id: number | null; name: string | null; email: string }
  role: OrganizationRole
  project_access: ProjectAccessScope
  invited: boolean
  /** The organization's billing user; the server refuses to remove or demote them. */
  billing_user: boolean
  invitation_id?: number | null
}

export const isOrganizationManager = (role: OrganizationRole | null | undefined): boolean =>
  role === 'owner' || role === 'admin'
